import React, { useState, useEffect } from "react";
import Axios from "axios";
import "../../css/style.css";
import Constant from "../Constant";
import { getAccessToken } from "../../hooks/AccessToken";
import EditPayment from "./EditPayment";

const Payment = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedPaymentType, setSelectedPaymentType] = useState("");
  const [selectedPaymentFor, setSelectedPaymentFor] = useState("");
  const [paymentForOptions, setPaymentForOptions] = useState([]);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  // Fixed payment types update from the payment schema
  const paymentTypeOptions = [
    "registration",
    "event",
    "contest",
    "story",
    "poem",
  ];

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const response = await Axios.get(
        `${Constant.apiBasePath}payment/all?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        }
      );

      setPayments(response.data.payments);
      setTotal(response.data.pagination.total);
      setTotalPages(response.data.pagination.pages);

      // Update payment for options based on all available data
      updatePaymentForOptions(response.data.payments, selectedPaymentType);

      console.log("Payments fetched:", response.data.payments);
    } catch (error) {
      console.log("Error with getting payments", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, limit]);

  const deletePayment = async (paymentId) => {
    try {
      setDeletingPaymentId(paymentId);
      await Axios.delete(`${Constant.apiBasePath}payment/${paymentId}`);
      await fetchPayments();
    } catch (error) {
      console.log("Error with deleting payment", error);
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const confirmDeletePayment = (paymentId) => {
    if (window.confirm("Are you sure you want to delete this payment?")) {
      deletePayment(paymentId);
    }
  };

  // Update payment for options based on selected payment type
  const updatePaymentForOptions = (paymentsData, paymentType) => {
    if (!paymentType) {
      setPaymentForOptions([]);
      return;
    }

    // Filter payments by the selected payment type
    const filteredPayments = paymentsData.filter(
      (payment) => payment.paymentType === paymentType
    );

    // Extract unique payment for options from the filtered payments
    const forOptions = [
      ...new Set(filteredPayments.map((payment) => payment.paymentFor)),
    ];
    setPaymentForOptions(forOptions);

    // Reset the selected payment for if it's not in the new options
    if (selectedPaymentFor && !forOptions.includes(selectedPaymentFor)) {
      setSelectedPaymentFor("");
    }
  };

  useEffect(() => {
    // When payment type changes, update the payment for options
    updatePaymentForOptions(payments, selectedPaymentType);
  }, [selectedPaymentType]);

  // Filter payments based on search, payment type, and payment for
  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      search.trim() === "" ||
      payment.userName.toLowerCase().includes(search.toLowerCase());

    const matchesType =
      selectedPaymentType === "" || payment.paymentType === selectedPaymentType;

    const matchesFor =
      selectedPaymentFor === "" || payment.paymentFor === selectedPaymentFor;

    return matchesSearch && matchesType && matchesFor;
  });

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1); // Reset to first page when changing limit
  };

  const handlePaymentTypeChange = (e) => {
    setSelectedPaymentType(e.target.value);
    setSelectedPaymentFor(""); // Reset payment for when type changes
  };

  const handlePaymentForChange = (e) => {
    setSelectedPaymentFor(e.target.value);
  };

  const handleUpdate = () => {
    fetchPayments();
    setEditingPaymentId(null);
  };

  return (
    <div className="content-page">
      <div className="content">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="page-title-heading">Payments</h4>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex flex-wrap">
                    <div className="me-3 mb-2" style={{ marginRight: "3rem" }}>
                      <input
                        type="text"
                        id="search"
                        className="form-control"
                        placeholder="Search by name..."
                        value={search}
                        onChange={handleSearch}
                      />
                    </div>
                    <div className="me-3 mb-2" style={{ marginRight: "3rem" }}>
                      <label htmlFor="paymentType" className="form-label">
                        Payment Type
                      </label>
                      <select
                        id="paymentType"
                        className="form-select"
                        value={selectedPaymentType}
                        onChange={handlePaymentTypeChange}
                      >
                        <option value="">All Types</option>
                        {paymentTypeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-2">
                      <label htmlFor="paymentFor" className="form-label">
                        Payment For
                      </label>
                      <select
                        id="paymentFor"
                        className="form-select"
                        value={selectedPaymentFor}
                        onChange={handlePaymentForChange}
                        disabled={
                          !selectedPaymentType || paymentForOptions.length === 0
                        }
                      >
                        <option value="">All Purposes</option>
                        {paymentForOptions.map((forOption) => (
                          <option key={forOption} value={forOption}>
                            {forOption}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden"></span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive table-wrapper" style={{ overflowX: 'auto', minWidth: 0 }}>
                      <table className="table dt-responsive nowrap">
                        <thead>
                          <tr>
                            <th>Order ID</th>
                            <th>Name</th>
                            <th>Amount</th>
                            <th>Type</th>
                            <th>For</th>
                            <th>Payment ID</th>
                            <th>Date</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPayments.length > 0 ? (
                            filteredPayments.map((payment) => (
                              <tr key={payment._id}>
                                <td>{payment.OrderId}</td>
                                <td>{payment.userName}</td>
                                <td>{payment.amount}</td>
                                <td>
                                  {payment.paymentType.charAt(0).toUpperCase() +
                                    payment.paymentType.slice(1)}
                                </td>
                                <td>{payment.paymentFor}</td>
                                <td>{payment.payment_id || "N/A"}</td>
                                <td>
                                  {new Date(
                                    payment.PaymentDate
                                  ).toLocaleDateString()}
                                </td>
                                <td>
                                  <span
                                    className={`badge ${
                                      payment.payment_status === "Completed"
                                        ? "bg-success"
                                        : "bg-warning"
                                    }`}
                                  >
                                    {payment.payment_status}
                                  </span>
                                </td>
                                <td>
                                  <div className="btn-group">
                                    <button
                                      onClick={() =>
                                        confirmDeletePayment(payment._id)
                                      }
                                      className="btn btn-danger btn-sm me-2"
                                      disabled={
                                        deletingPaymentId === payment._id
                                      }
                                    >
                                      {deletingPaymentId === payment._id ? (
                                        <span
                                          className="spinner-border spinner-border-sm"
                                          role="status"
                                          aria-hidden="true"
                                        ></span>
                                      ) : (
                                        "Delete"
                                      )}
                                    </button>
                                    <button
                                      onClick={() =>
                                        setEditingPaymentId(payment._id)
                                      }
                                      className="btn btn-primary btn-sm"
                                      disabled={
                                        editingPaymentId === payment._id
                                      }
                                    >
                                      Edit
                                    </button>
                                  </div>

                                  {editingPaymentId === payment._id && (
                                    <EditPayment
                                      id={payment._id}
                                      userName={payment.userName}
                                      paymentType={payment.paymentType}
                                      paymentFor={payment.paymentFor}
                                      onClose={() => setEditingPaymentId(null)}
                                      onUpdate={handleUpdate}
                                    />
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="9" className="text-center">
                                No payments found matching the criteria
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        <select
                          className="form-select form-select-sm"
                          value={limit}
                          onChange={handleLimitChange}
                        >
                          <option value="5">5 per page</option>
                          <option value="10">10 per page</option>
                          <option value="25">25 per page</option>
                          <option value="50">50 per page</option>
                        </select>
                      </div>

                      <div>
                        <span className="me-3">
                          Page {page} of {totalPages} (Total: {total} records)
                        </span>

                        <div className="btn-group">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(1)}
                            disabled={page === 1}
                          >
                            First
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(page - 1)}
                            disabled={page === 1}
                          >
                            Prev
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(page + 1)}
                            disabled={page === totalPages}
                          >
                            Next
                          </button>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handlePageChange(totalPages)}
                            disabled={page === totalPages}
                          >
                            Last
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
